/** Record identity for remote autocompletes — the console rule: every option of a
 *  tenant-owned record shows its name AND its primary key AND its tenant, in the
 *  dropdown and in the selected value/chips. A bare name is not enough — two
 *  tenants routinely own records of the same name ("VIP", "Morning Walk").
 *
 *  Pass `getOptionMeta` to `RemoteAutocomplete` (it then renders the two-line
 *  option below and derives the input/chip text via {@link recordLabel}); global
 *  records pass `tenant: null`, which renders as "global".
 *  Rule + examples: aidocs/context/components.md → "Remote autocompletes show tenant + pk".
 */
import { Box, Typography } from '@mui/material';
import type { HTMLAttributes } from 'react';

export interface RecordMeta {
  /** Display name (first line). */
  name: string;
  /** Primary key. */
  pk: number | string;
  /** Tenant code; `null` for a global record, `undefined` when unknown. */
  tenant?: string | null;
  /** Extra disambiguators (custom id, codename, dates…), shown after the tenant. */
  extra?: Array<string | null | undefined>;
}

function tenantText(tenant: string | null | undefined): string | null {
  if (tenant === undefined) return null;
  return tenant ?? 'global';
}

/** Single-line identity for the input text and chips: `Name · #12 · CODE`. */
export function recordLabel(meta: RecordMeta): string {
  const bits = [meta.name || `#${meta.pk}`, `#${meta.pk}`];
  const t = tenantText(meta.tenant);
  if (t) bits.push(t);
  return bits.join(' · ');
}

/** Second line: `#12 · CODE · extra…`. */
export function recordSubline(meta: RecordMeta): string {
  const bits: string[] = [`#${meta.pk}`];
  const t = tenantText(meta.tenant);
  if (t) bits.push(t);
  for (const x of meta.extra ?? []) if (x) bits.push(x);
  return bits.join(' · ');
}

export function RecordOption({
  props,
  meta,
}: {
  props: HTMLAttributes<HTMLLIElement> & { key?: string };
  meta: RecordMeta;
}) {
  const { key, ...rest } = props;
  return (
    <li key={key} {...rest}>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" noWrap>
          {meta.name || `#${meta.pk}`}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap component="div">
          {recordSubline(meta)}
        </Typography>
      </Box>
    </li>
  );
}
